import React, { useState, useEffect, useCallback } from 'react';
import './PracticeSetupWizard.css';
import NewPracticeForm, { PROVIDER_TYPES, validateNpi } from './NewPracticeForm';

const API_URL = process.env.REACT_APP_API_URL || '';

// The New Practice workflow (docs/onboarding.md), run with the practice on a call. The
// first two steps fill in the practice; leaving Integrations creates it, and the rest
// set it up in the same window. Providers come after the EMR pull so they can be checked
// against the data. Every step saves as it goes, so closing and reopening from Practice
// Details resumes.
const CREATE_STEPS = [
  { key: 'info', label: 'Practice info' },
  { key: 'integrations', label: 'Integrations' },
];
const SETUP_STEPS = [
  { key: 'emr', label: 'Data' },
  { key: 'providers', label: 'Providers' },
  { key: 'payers', label: 'Payers' },
  { key: 'fee_schedules', label: 'Fee schedules' },
  { key: 'policies', label: 'Emails & reminders' },
  { key: 'summary', label: 'Summary' },
];
const STEPS = [...CREATE_STEPS, ...SETUP_STEPS];
const isCreateStep = (key) => CREATE_STEPS.some(s => s.key === key);

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const WEEKDAYS = [1, 2, 3, 4, 5];
const daysMode = (days) => (days.join() === WEEKDAYS.join() ? 'weekdays' : days.length === 7 ? 'every' : 'custom');
const EMR_LABELS = {
  ecw: 'eClinicalWorks', tebra: 'Tebra', practicefusion: 'Practice Fusion', kinnser: 'Kinnser / WellSky', advancedmd: 'AdvancedMD',
  athena: 'Athena', modmed: 'ModMed', demo: 'Demo (sample data)',
};

const splitEmails = (text) => text.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);

async function api(path, options = {}) {
  const response = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  let data = null;
  try { data = await response.json(); } catch { data = null; }
  if (!response.ok) throw new Error((data && data.error) || `Request failed (${response.status})`);
  return data;
}

const jsonBody = (method, body) => ({
  method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});

// practiceId: an existing practice (resume from Practice Details), or none to start a new
// one. canOnboard false (setup routes off) ends the workflow once the practice is created.
// onClose(practice) gets the practice created here, if any.
function PracticeSetupWizard({ practiceId, onClose, initialStep, canOnboard = true, onPracticesUpdated }) {
  const [pid, setPid] = useState(practiceId || null);
  const [created, setCreated] = useState(null);
  const [setup, setSetup] = useState(null);
  const [step, setStep] = useState(practiceId ? (initialStep || 'emr') : 'info');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const base = `/api/onboarding/practices/${pid}/setup`;

  const load = useCallback(async () => {
    if (!pid) return;
    try {
      setSetup(await api(base));
    } catch (err) {
      setError(err.message);
    }
  }, [pid, base]);

  useEffect(() => { load(); }, [load]);

  const close = () => onClose(created);

  const handleCreated = async (practice, logoFile) => {
    setCreated(practice);
    let logoError = null;
    if (logoFile) {
      // The logo route needs the practice's id, so the file chosen on Practice info goes up now.
      const body = new FormData();
      body.append('file', logoFile);
      try {
        await api(`/api/practices/${practice.id}/logo`, { method: 'PUT', body });
      } catch (err) {
        logoError = `${practice.name} was created, but the logo did not upload: ${err.message}`;
      }
    }
    if (!canOnboard) { onClose(practice); return; }
    setPid(practice.id);
    if (logoError) setError(logoError);
    else setNotice(`${practice.name} created. Keep going to set it up.`);
    setStep('emr');
  };

  // Every write answers with the whole wizard state, so one helper covers them all.
  const run = async (path, options, successText) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const data = await api(path, options);
      // Only a setup state replaces it: an added provider carries practice_id too.
      if (data && Array.isArray(data.steps)) setSetup(data);
      if (successText) setNotice(typeof successText === 'function' ? successText(data) : successText);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Nothing is saved per step: a step's dot turns green once its checks pass.
  const goNext = (currentKey) => {
    const i = STEPS.findIndex(s => s.key === currentKey);
    setNotice(null);
    setStep(STEPS[Math.min(i + 1, STEPS.length - 1)].key);
  };

  const done = new Set(setup ? setup.steps_done : []);
  const stepState = (key) => {
    // Before the practice exists only the create steps up to the current one are open;
    // after, they are done (edited on Practice Details) and setup is open.
    if (isCreateStep(key)) {
      if (pid) return { done: true, disabled: true };
      const at = CREATE_STEPS.findIndex(s => s.key === step);
      return { done: false, disabled: CREATE_STEPS.findIndex(s => s.key === key) > at };
    }
    return { done: done.has(key), disabled: !pid };
  };
  const stepProps = { setup, base, run, busy, practiceId: pid };
  const name = setup ? setup.practice_name : (created && created.name) || 'practice';

  return (
    <div className="modal-overlay">
      <div className="setup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="setup-header">
          <div>
            <h2>{pid ? `Set up ${name}` : 'New Practice'}</h2>
          </div>
          <button type="button" className="setup-close" onClick={close} aria-label="Close">×</button>
        </div>

        {/* Only the current step and the next one are named; the rest are dots (hover
            for the name), so the bar stays one short line. */}
        <div className="setup-steps">
          {STEPS.map((s, i) => {
            const st = stepState(s.key);
            const at = STEPS.findIndex(x => x.key === step);
            const named = i === at || i === at + 1;
            const go = () => { setError(null); setNotice(null); setStep(s.key); };
            if (!named) {
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={st.disabled}
                  className={`setup-dot${st.done ? ' done' : ''}`}
                  onClick={go}
                  title={`${i + 1}. ${s.label}`}
                  aria-label={`Step ${i + 1}: ${s.label}`}
                />
              );
            }
            return (
              <button
                key={s.key}
                type="button"
                disabled={st.disabled}
                className={`setup-step${i === at ? ' active' : ' next'}${st.done ? ' done' : ''}`}
                onClick={go}
              >
                <span className="setup-step-num">{st.done && i !== at ? '✓' : i + 1}</span>
                {i === at ? s.label : `Next: ${s.label}`}
              </button>
            );
          })}
          <span className="setup-step-count">Step {STEPS.findIndex(x => x.key === step) + 1} of {STEPS.length}</span>
        </div>

        {error && (
          <div className="form-message error dismissable">
            <span className="message-content">{error}</span>
            <button type="button" className="message-close" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {notice && (
          <div className="form-message success dismissable">
            <span className="message-content">{notice}</span>
            <button type="button" className="message-close" onClick={() => setNotice(null)}>&times;</button>
          </div>
        )}

        {!pid ? (
          <NewPracticeForm
            step={CREATE_STEPS.findIndex(s => s.key === step) + 1}
            setStep={(n) => setStep(CREATE_STEPS[n - 1].key)}
            onCancel={close}
            onCreated={handleCreated}
            onPracticesUpdated={onPracticesUpdated}
          />
        ) : !setup ? (
          <div className="setup-body setup-muted">Loading setup…</div>
        ) : (
          <>
            <div className="setup-body">
              {step === 'emr' && <EmrStep {...stepProps} />}
              {step === 'providers' && <ProvidersStep {...stepProps} />}
              {step === 'payers' && <PayersStep {...stepProps} />}
              {step === 'fee_schedules' && <FeeSchedulesStep {...stepProps} />}
              {step === 'policies' && <PoliciesStep {...stepProps} />}
              {step === 'summary' && <SummaryStep setup={setup} onGo={setStep} />}
            </div>

            <div className="modal-actions">
              {step === 'summary' ? (
                <>
                  <button type="button" className="btn btn-secondary" onClick={() => { setNotice(null); setStep('policies'); }}>
                    Back
                  </button>
                  <button type="button" className="btn btn-primary" onClick={close}>Finish</button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-secondary" onClick={close}>Save and close</button>
                  {step !== SETUP_STEPS[0].key && (
                    <button type="button" className="btn btn-secondary" onClick={() => { setNotice(null); setStep(STEPS[STEPS.findIndex(x => x.key === step) - 1].key); }}>
                      Back
                    </button>
                  )}
                  <button type="button" className="btn btn-primary" disabled={busy} onClick={() => goNext(step)}>
                    Next
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// --- EMR --------------------------------------------------------------------------

function EmrStep({ setup, base, run, busy, practiceId }) {
  const { emr } = setup;
  const rows = (emr.checklist && emr.checklist.rows) || [];
  const label = EMR_LABELS[emr.system] || emr.system;

  const firstPull = (kind) => run(
    `/api/onboarding/practices/${practiceId}/feeds/${kind}/first-pull`, { method: 'POST' },
    (d) => (d && d.queued === false ? `Not in the app yet: ${d.reason}` : 'First pull finished.'),
  ).then(() => run(base, {}));

  return (
    <div>

      <div className="setup-card">
        <div className="setup-card-row">
          <div>
            <div className="setup-card-title">{emr.system ? label : 'No EMR chosen'}</div>
            <div className="setup-muted">
              {!emr.system && 'Choose the EMR on Practice Details → Integrations, or load demo data below.'}
              {emr.pushed && !emr.connected && "Data arrives once the Kinnser daily sync is pointed at this practice."}
              {emr.system === 'advancedmd' && !emr.connected && "Upload the AdvancedMD reports on the practice's Data page (AdvancedMD reports card)."}
              {emr.system && emr.system !== 'advancedmd' && !emr.has_api && !emr.pushed && 'Atlas does not connect to this EMR yet. Data comes in by upload.'}
              {emr.system && emr.has_api && !emr.connected && 'Credentials are not saved yet. Add them on Practice Details → Integrations.'}
              {emr.connected && `${emr.claims} claims in Atlas so far.`}
            </div>
          </div>
          <span className={`status-badge ${emr.connected ? 'active' : 'inactive'}`}>{emr.connected ? 'Connected' : 'Not connected'}</span>
        </div>
      </div>

      {emr.demo_allowed && (emr.system === 'demo' || !emr.claims) && (
        <div className="setup-card">
          <div className="setup-card-row">
            <div>
              <div className="setup-card-title">Demo data</div>
              <div className="setup-muted">
                Local only. Fills this practice with fake patients, claims, payments and appointments,
                standing in for a first EMR pull.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => run(`${base}/demo-data`, { method: 'POST' },
                (d) => `Loaded ${d.result.claims} claims, ${d.result.patients} patients, ${d.result.appointments} appointments.`)}
            >
              {busy ? 'Loading…' : emr.system === 'demo' ? 'Reload demo data' : 'Load demo data'}
            </button>
          </div>
        </div>
      )}

      {emr.system && emr.system !== 'demo' && rows.length > 0 && (
        <table className="detail-table setup-table">
          <thead>
            <tr><th>Feed</th><th>Status</th><th>Last pull</th><th /></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.feed_kind}>
                <td>{r.label}{r.required ? '' : <span className="setup-muted"> (optional)</span>}</td>
                <td>
                  <span className={`status-badge ${r.status === 'first_pull_ok' ? 'active' : 'inactive'}`}>
                    {r.status.replace(/_/g, ' ')}
                  </span>
                  <div className="setup-muted setup-small">{r.status_detail}</div>
                </td>
                <td>{r.last_rows != null ? `${r.last_rows} rows` : '—'}</td>
                <td>
                  {r.first_pull && r.first_pull.available && (
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => firstPull(r.feed_kind)}>
                      Run first pull
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Providers --------------------------------------------------------------------

const EMPTY_PROVIDER = { first_name: '', last_name: '', npi: '', provider_type: '', taxonomy_code: '', taxonomy: '', gender: '' };

// "TONG, CATHY NP" -> first Cathy, last Tong; "Cathy Tong MD" -> first Cathy, last Tong.
function splitProviderName(name) {
  const titled = (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  const creds = new Set(PROVIDER_TYPES.map(t => t.toLowerCase()).concat(['do', 'fnp', 'pmhnp', 'phd', 'psyd', 'rn']));
  if (name.includes(',')) {
    const [last, rest] = name.split(',', 2);
    const first = rest.trim().split(/\s+/).filter(w => !creds.has(w.toLowerCase().replace(/\./g, '')))[0] || '';
    return { first_name: titled(first), last_name: last.trim().split(/\s+/).map(titled).join(' ') };
  }
  const words = name.trim().split(/\s+/).filter(w => !creds.has(w.toLowerCase().replace(/\./g, '')));
  return { first_name: titled(words[0] || ''), last_name: words.slice(1).map(titled).join(' ') };
}

// Same person when the last names agree and the first names share an initial
// ("Kathy" / "Katherine" both pass; a different surname does not).
function sameName(row, registry) {
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z]/g, '');
  return norm(row.last_name) === norm(registry.last_name)
    && norm(row.first_name).charAt(0) === norm(registry.first_name).charAt(0);
}

// One editable row per provider not yet added: the names found in the EMR data first
// (pre-filled), then any added by hand. The registry finds a row's NPI by name and checks
// a typed NPI exists and belongs to that name.
function ProvidersStep({ setup, base, run, busy, practiceId }) {
  const { configured, in_data: inData } = setup.providers;
  const [rows, setRows] = useState({});
  const [manual, setManual] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  const suggestions = inData.filter(r => !r.matched && !dismissed.has(r.name));
  const blank = (name) => ({ ...EMPTY_PROVIDER, ...(name ? splitProviderName(name) : {}) });
  const rowFor = (key, name) => rows[key] || blank(name);
  // Functional update: lookups resolve after other edits, so never patch a stale copy.
  const patch = (key, name, change) => setRows(rs => ({ ...rs, [key]: { ...(rs[key] || blank(name)), ...change } }));

  const checkNpi = async (key, name, row) => {
    if (row.npi.length !== 10 || validateNpi(row.npi)) return;
    patch(key, name, { check: { state: 'checking' } });
    try {
      const response = await fetch(`${API_URL}/api/npi-lookup/${row.npi}?practice_id=${practiceId}`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (response.status === 404) { patch(key, name, { check: { state: 'missing' } }); return; }
      if (!response.ok) throw new Error(data.error);
      if (data.type !== 'individual') { patch(key, name, { check: { state: 'organization', text: data.name } }); return; }
      const label = `${data.first_name} ${data.last_name}${data.credential ? `, ${data.credential}` : ''}`;
      patch(key, name, {
        first_name: row.first_name.trim() ? row.first_name : data.first_name || '',
        last_name: row.last_name.trim() ? row.last_name : data.last_name || '',
        provider_type: data.provider_type || row.provider_type || '',
        taxonomy_code: data.taxonomy_code || '',
        taxonomy: data.taxonomy_desc || '',
        gender: data.gender || '',
        check: { state: !row.last_name.trim() || sameName(row, data) ? 'match' : 'mismatch', text: label },
      });
    } catch {
      patch(key, name, { check: { state: 'unreachable' } });
    }
  };

  const findNpi = async (key, name, row) => {
    patch(key, name, { candidates: null, searching: true });
    try {
      const q = new URLSearchParams({ first_name: row.first_name, last_name: row.last_name });
      const data = await api(`${base}/npi-search?${q}`);
      patch(key, name, { candidates: data.results, searching: false });
    } catch (err) {
      patch(key, name, { candidates: [], searchError: err.message, searching: false });
    }
  };

  const pickCandidate = (key, name, c) => patch(key, name, {
    first_name: c.first_name, last_name: c.last_name, npi: c.npi,
    provider_type: c.provider_type || '', taxonomy_code: c.taxonomy_code || '', taxonomy: c.taxonomy || '',
    gender: c.gender || '', candidates: null,
    check: { state: 'match', text: `${c.first_name} ${c.last_name}${c.credential ? `, ${c.credential}` : ''}` },
  });

  const add = async (key, name, row, onAdded) => {
    const npiErr = validateNpi(row.npi);
    if (npiErr) { patch(key, name, { rowError: `NPI: ${npiErr}` }); return; }
    if (!row.first_name.trim() && !row.last_name.trim()) { patch(key, name, { rowError: 'Enter a name' }); return; }
    if (row.check && (row.check.state === 'missing' || row.check.state === 'organization')) {
      patch(key, name, { rowError: 'That NPI is not an individual provider in the registry' });
      return;
    }
    const ok = await run(`/api/practices/${practiceId}/providers`, jsonBody('POST', {
      first_name: row.first_name || null,
      last_name: row.last_name || null,
      npi: row.npi || null,
      provider_type: row.provider_type || null,
      taxonomy_code: row.taxonomy_code || null,
      gender: row.gender || null,
    }), `Added ${row.first_name} ${row.last_name}.`);
    if (ok !== null) {
      onAdded();
      run(base, {});
    }
  };

  const checkIcon = (check) => {
    if (!check) return null;
    switch (check.state) {
      case 'checking': return <span className="setup-muted" title="Checking">…</span>;
      case 'match': return <span className="setup-npi-ok" title={check.text}>✓</span>;
      case 'mismatch': return <span className="setup-npi-warn" title={`Registry: ${check.text}`}>!</span>;
      case 'missing': return <span className="setup-npi-bad" title="Not in the NPI registry">✗</span>;
      case 'organization': return <span className="setup-npi-bad" title={`Organization NPI${check.text ? `: ${check.text}` : ''}`}>✗</span>;
      default: return <span className="setup-muted" title="Registry unreachable">?</span>;
    }
  };

  const editableRow = (key, name, meta, onAdded, onRemove) => {
    const row = rowFor(key, name);
    const set = (field) => (e) => {
      const value = field === 'npi' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value;
      patch(key, name, { [field]: value, rowError: null, ...(field === 'npi' ? { check: null, taxonomy_code: '', taxonomy: '', gender: '' } : field.endsWith('_name') ? { check: null } : field === 'taxonomy_code' ? { taxonomy: '' } : {}) });
      // The tenth digit looks the NPI up and fills the rest of the row.
      if (field === 'npi' && value.length === 10 && value !== row.npi) checkNpi(key, name, { ...row, npi: value });
    };
    return (
      <React.Fragment key={key}>
        <tr>
          <td>
            <div className="setup-npi-cell">
              <input className="setup-cell-input" value={row.npi} onChange={set('npi')} placeholder="10 digits" maxLength={10} />
              <span className="setup-npi-icon">{checkIcon(row.check)}</span>
            </div>
          </td>
          <td>
            <input className="setup-cell-input" value={row.first_name} onChange={set('first_name')} placeholder="First" />
            {meta && <div className="setup-muted setup-small">{meta}</div>}
          </td>
          <td><input className="setup-cell-input" value={row.last_name} onChange={set('last_name')} placeholder="Last" /></td>
          <td>
            <select className="setup-cell-input" value={row.provider_type} onChange={set('provider_type')}>
              <option value="">--</option>
              {PROVIDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </td>
          <td>
            <input className="setup-cell-input" value={row.taxonomy_code} onChange={set('taxonomy_code')} placeholder="Code" maxLength={10} title={row.taxonomy} />
          </td>
          <td>
            <div className="setup-row-actions">
              <button type="button" className="btn btn-secondary btn-sm" disabled={row.searching || !row.last_name.trim()} onClick={() => findNpi(key, name, row)}>
                {row.searching ? 'Searching…' : 'Find NPI'}
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => add(key, name, row, onAdded)}>Add</button>
              <button type="button" className="setup-link" onClick={onRemove} aria-label="Remove">&times;</button>
            </div>
          </td>
        </tr>
        {row.rowError && (
          <tr><td colSpan={6}><span className="setup-npi-bad">{row.rowError}</span></td></tr>
        )}
        {row.candidates && (
          <tr>
            <td colSpan={6}>
              {row.candidates.length === 0 ? (
                <span className="setup-muted">{row.searchError || `No match${setup.practice_state ? ` in ${setup.practice_state}` : ''}`}</span>
              ) : (
                <ul className="setup-candidates">
                  {row.candidates.map(c => (
                    <li key={c.npi}>
                      <span>{c.npi}</span>
                      <span>{c.first_name} {c.last_name}{c.credential ? `, ${c.credential}` : ''}</span>
                      <span className="setup-muted">{[c.taxonomy, [c.city, c.state].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</span>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => pickCandidate(key, name, c)}>Use</button>
                    </li>
                  ))}
                </ul>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const counts = (r) => [
    r.claims ? `${r.claims} claim${r.claims === 1 ? '' : 's'}` : null,
    r.appointments ? `${r.appointments} appt${r.appointments === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <table className="detail-table setup-table">
        <thead><tr><th>NPI</th><th>First name</th><th>Last name</th><th>Type</th><th>Taxonomy</th><th /></tr></thead>
        <tbody>
          {configured.map(p => (
            <tr key={`p${p.id}`}>
              <td>{p.npi || '—'}</td>
              <td>{p.first_name || '—'}</td>
              <td>{p.last_name || '—'}</td>
              <td>{p.provider_type || '—'}</td>
              <td>{p.taxonomy_code || '—'}</td>
              <td><span className="status-badge active">Added</span></td>
            </tr>
          ))}
          {suggestions.map(r => editableRow(
            `s:${r.name}`, r.name, counts(r),
            () => setDismissed(d => new Set(d).add(r.name)),
            () => setDismissed(d => new Set(d).add(r.name)),
          ))}
          {manual.map(id => editableRow(
            `m:${id}`, null, null,
            () => setManual(m => m.filter(x => x !== id)),
            () => setManual(m => m.filter(x => x !== id)),
          ))}
          {configured.length === 0 && suggestions.length === 0 && manual.length === 0 && (
            <tr><td colSpan={6} className="setup-muted">No providers yet</td></tr>
          )}
        </tbody>
      </table>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setManual(m => [...m, Date.now()])}>
        + Add provider
      </button>
    </div>
  );
}

// --- Payers -----------------------------------------------------------------------

function PayersStep({ setup, base, run, busy, practiceId }) {
  const { suggested, configured } = setup.payers;
  const [picked, setPicked] = useState({});
  const [manual, setManual] = useState({ payer_name: '', payer_id: '' });
  const [portal, setPortal] = useState({});

  const addPicked = () => {
    const payers = suggested.filter((s, i) => picked[i]).map(s => ({ payer_id: s.payer_id, payer_name: s.payer_name }));
    if (!payers.length) return;
    run(`${base}/payers`, jsonBody('POST', { payers }), (d) => `Added ${d.result.added} payer(s).`)
      .then(() => setPicked({}));
  };

  const addManual = () => {
    if (!manual.payer_name.trim()) return;
    run(`${base}/payers`, jsonBody('POST', { payers: [manual] }), (d) => (d.result.added ? 'Payer added.' : 'That payer is already on the practice.'))
      .then(() => setManual({ payer_name: '', payer_id: '' }));
  };

  const portalValue = (p, key) => (portal[p.id] && portal[p.id][key] !== undefined ? portal[p.id][key] : (p[key] || ''));
  const setPortalField = (id, key, value) => setPortal(s => ({ ...s, [id]: { ...s[id], [key]: value } }));

  // Availity / OneHealthcare ID: the practice's shared login, so the payer keeps none.
  // Same rule as the server: the host is on the portal's domain (www., a path or a
  // trailing slash still counts).
  const sharedPortal = (url) => {
    let host = '';
    try { host = new URL(/:\/\//.test(url || '') ? url : `https://${url}`).hostname.toLowerCase(); } catch { return undefined; }
    return setup.payers.shared_portals.find(sp => host === sp.domain || host.endsWith(`.${sp.domain}`));
  };
  const pickPortal = (p, key) => {
    const sp = setup.payers.shared_portals.find(x => x.key === key);
    // A shared portal has nothing else to fill in, so it saves (and counts) at once.
    if (sp) { savePortal(p, { portal_url: sp.url, portal_username: '' }); return; }
    setPortal(s => ({
      ...s,
      [p.id]: { ...s[p.id], portal_url: '', other: key === 'other' },
    }));
  };

  const savePortal = async (p, override) => {
    const edits = override || portal[p.id] || {};
    const body = {};
    ['portal_url', 'portal_username', 'portal_password'].forEach(k => { if (edits[k] !== undefined) body[k] = edits[k]; });
    if (sharedPortal(body.portal_url)) body.clear_portal_password = true;
    if (!Object.keys(body).length) return;
    const ok = await run(`/api/practices/${practiceId}/payers/${p.id}`, jsonBody('PUT', body), `Portal login saved for ${p.payer_name}.`);
    if (ok !== null) {
      setPortal(s => ({ ...s, [p.id]: undefined }));
      run(base, {});
    }
  };

  return (
    <div>

      <h3 className="setup-h3">Found on claims</h3>
      {suggested.length === 0 ? (
        <div className="setup-muted">No claims yet. Pull from the EMR first, or add payers by hand below.</div>
      ) : (
        <>
          <table className="detail-table setup-table">
            <thead><tr><th /><th>Payer</th><th>Payer ID</th><th>Claims</th><th>Status</th></tr></thead>
            <tbody>
              {suggested.map((s, i) => (
                <tr key={`${s.payer_id}-${s.payer_name}`}>
                  <td>
                    {!s.configured && (
                      <input type="checkbox" checked={!!picked[i]} onChange={(e) => setPicked(p => ({ ...p, [i]: e.target.checked }))} />
                    )}
                  </td>
                  <td>{s.payer_name || '—'}</td>
                  <td>{s.payer_id || '—'}</td>
                  <td>{s.claims}</td>
                  <td><span className={`status-badge ${s.configured ? 'active' : 'inactive'}`}>{s.configured ? 'Added' : 'Not added'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {suggested.some(s => !s.configured) && (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={addPicked}>Add selected</button>
          )}
        </>
      )}

      <h3 className="setup-h3">Add a payer</h3>
      <div className="form-row">
        <div className="form-field">
          <label>Payer name</label>
          <input value={manual.payer_name} onChange={(e) => setManual(m => ({ ...m, payer_name: e.target.value }))} placeholder="e.g., Aetna" />
        </div>
        <div className="form-field">
          <label>Payer ID</label>
          <input value={manual.payer_id} onChange={(e) => setManual(m => ({ ...m, payer_id: e.target.value }))} placeholder="e.g., 60054" />
        </div>
        <div className="form-field setup-field-button">
          <button type="button" className="btn btn-secondary" disabled={busy || !manual.payer_name.trim()} onClick={addManual}>Add</button>
        </div>
      </div>

      <h3 className="setup-h3">Portal logins</h3>
      {configured.length === 0 ? (
        <div className="setup-muted">Add payers first.</div>
      ) : (
        <table className="detail-table setup-table">
          <thead><tr><th>Payer</th><th>Portal URL</th><th>Username</th><th>Password</th><th /></tr></thead>
          <tbody>
            {configured.map(p => {
              const shared = sharedPortal(portalValue(p, 'portal_url'));
              const other = !shared && (portalValue(p, 'portal_url') || (portal[p.id] && portal[p.id].other));
              return (
              <tr key={p.id}>
                <td>{p.payer_name}</td>
                <td>
                  <div className="setup-portal-cell">
                    <select className="setup-input" value={shared ? shared.key : other ? 'other' : ''} onChange={(e) => pickPortal(p, e.target.value)}>
                      <option value="">--</option>
                      {setup.payers.shared_portals.map(sp => <option key={sp.key} value={sp.key}>{sp.label}</option>)}
                      <option value="other">Other URL</option>
                    </select>
                    {other && (
                      <input className="setup-input" value={portalValue(p, 'portal_url')} onChange={(e) => setPortalField(p.id, 'portal_url', e.target.value)} placeholder="https://" />
                    )}
                  </div>
                </td>
                <td><input className="setup-input" value={shared ? '' : portalValue(p, 'portal_username')} disabled={!!shared} onChange={(e) => setPortalField(p.id, 'portal_username', e.target.value)} autoComplete="off" /></td>
                <td>
                  <input
                    className="setup-input"
                    type="password"
                    value={shared ? '' : (portal[p.id] && portal[p.id].portal_password) || ''}
                    disabled={!!shared}
                    onChange={(e) => setPortalField(p.id, 'portal_password', e.target.value)}
                    placeholder={!shared && p.portal_password_set ? 'Saved (type to replace)' : ''}
                    autoComplete="new-password"
                  />
                </td>
                <td>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={busy || !portal[p.id]} onClick={() => savePortal(p)}>Save</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Fee schedules ----------------------------------------------------------------

// Drafts are stored as "Inferred: <payer>"; the Draft badge already says so.
const scheduleLabel = (name) => name.replace(/^Inferred: /, '');

function FeeSchedulesStep({ setup, base, run, busy, practiceId }) {
  const { fee_schedules: fees } = setup;
  const [open, setOpen] = useState(null);
  const [rates, setRates] = useState(null);
  const [ratesError, setRatesError] = useState(null);

  // Every schedule's rates in one read, while one is open; read again whenever setup
  // reloads (new drafts), so an open schedule never shows stale rates.
  useEffect(() => { setRates(null); }, [fees]);
  useEffect(() => {
    if (!open || rates) return;
    setRatesError(null);
    api(`/api/practices/${practiceId}/fee-schedule/payer-groups`)
      .then(data => setRates(data.rates || []))
      .catch(err => setRatesError(err.message));
  }, [open, rates, practiceId]);
  const toggle = (name) => setOpen(o => (o === name ? null : name));

  if (!fees.available) {
    return <div className="setup-muted">Fee schedules are not set up on this database yet.</div>;
  }

  const generate = () => run(`${base}/fee-schedules/generate`, { method: 'POST' }, (d) => {
    const r = d.result;
    if (!r.schedules) return 'No new drafts: every payer with payment history already has a schedule, or there is not enough history yet.';
    return `Drafted ${r.schedules} schedule(s) with ${r.rates} rates from payment history.`;
  });

  const confirm = (name, confirmed) => run(
    `${base}/fee-schedules/confirm`,
    jsonBody('POST', { schedule: name, confirmed }),
    confirmed ? `${scheduleLabel(name)} confirmed.` : `${scheduleLabel(name)} is a draft again.`,
  );

  return (
    <div>
      <button type="button" className="btn btn-secondary" disabled={busy} onClick={generate}>
        {busy ? 'Working…' : 'Draft schedules from payment history'}
      </button>

      {fees.schedules.length === 0 ? (
        <div className="setup-muted" style={{ marginTop: 14 }}>No fee schedules yet.</div>
      ) : (
        <table className="detail-table setup-table">
          <thead><tr><th>Schedule</th><th>Codes</th><th>Status</th><th /></tr></thead>
          <tbody>
            {fees.schedules.map(s => (
              <React.Fragment key={s.name}>
              <tr>
                <td>
                  <button type="button" className="setup-expand" onClick={() => toggle(s.name)} aria-expanded={open === s.name}>
                    <span className="setup-expand-caret">{open === s.name ? '▾' : '▸'}</span>
                    {scheduleLabel(s.name)}
                  </button>
                </td>
                <td>{s.codes}</td>
                <td><span className={`status-badge ${s.confirmed ? 'active' : 'inactive'}`}>{s.confirmed ? 'Confirmed' : 'Draft'}</span></td>
                <td>
                  {!s.inferred ? null : s.confirmed ? (
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => confirm(s.name, false)}>Undo</button>
                  ) : (
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => confirm(s.name, true)}>Confirm</button>
                  )}
                </td>
              </tr>
              {open === s.name && (
                <tr>
                  <td colSpan={4} className="setup-rates-cell">
                    {ratesError ? <span className="setup-npi-bad">{ratesError}</span>
                      : !rates ? <span className="setup-muted">Loading…</span> : (
                        <table className="setup-rates">
                          <thead><tr><th>CPT</th><th>Modifier</th><th>Rate</th><th>From</th></tr></thead>
                          <tbody>
                            {rates.filter(r => r.schedule_name === s.name).map(r => (
                              <tr key={r.id}>
                                <td>{r.cpt}</td>
                                <td>{r.modifier || '—'}</td>
                                <td>${r.rate}</td>
                                <td>{r.effective_from || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Emails & reminders -----------------------------------------------------------

function PoliciesStep({ setup, base, run, busy }) {
  const p = setup.policies;
  const [form, setForm] = useState(() => ({
    copay_email_recipients: p.copay_email_recipients.join(', '),
    copay_email_days: p.copay_email_days,
    copay_days_mode: daysMode(p.copay_email_days),
    weekly_report_recipients: p.weekly_report_recipients.join(', '),
    weekly_report_day: p.weekly_report_day || 1,
    weekly_report_hour: p.weekly_report_hour != null ? p.weekly_report_hour : 7,
  }));

  // Saved as it changes, like the other steps, and only the field that changed: picks
  // and checkboxes at once, the address boxes when they lose focus (if edited). The
  // first recipient switches an email on; clearing them switches it off.
  const [saved, setSaved] = useState({ copay: form.copay_email_recipients, weekly: form.weekly_report_recipients });
  const save = (body) => run(`${base}/policies`, jsonBody('PUT', body));
  const type = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const saveRecipients = (which) => {
    const key = which === 'copay' ? 'copay_email_recipients' : 'weekly_report_recipients';
    if (form[key] === saved[which]) return;
    setSaved(sv => ({ ...sv, [which]: form[key] }));
    save({ [key]: splitEmails(form[key]) });
  };
  const set = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    if (key === 'copay_days_mode' || key === 'copay_email_days') {
      const days = next.copay_days_mode === 'weekdays' ? WEEKDAYS
        : next.copay_days_mode === 'every' ? ALL_DAYS : next.copay_email_days;
      // Custom with nothing ticked is not a schedule yet; wait for a day.
      if (days.length) save({ copay_email_days: days });
    } else {
      save({ [key]: Number(value) });
    }
  };

  return (
    <div>
      <h3 className="setup-h3">Daily co-pay email</h3>
      <div className="form-row">
        <div className="form-field full">
          <label>Who gets it</label>
          <input value={form.copay_email_recipients} onChange={(e) => type('copay_email_recipients', e.target.value)} onBlur={() => saveRecipients('copay')} placeholder="frontdesk@practice.com, manager@practice.com" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Days</label>
          <select value={form.copay_days_mode} onChange={(e) => set('copay_days_mode', e.target.value)}>
            <option value="weekdays">Weekdays</option>
            <option value="every">Every day</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>
      {form.copay_days_mode === 'custom' && (
        <div className="setup-days">
          {DAYS.map((d, i) => (
            <label key={d} className="toggle-label">
              <input
                type="checkbox"
                checked={form.copay_email_days.includes(i + 1)}
                onChange={(e) => set('copay_email_days', e.target.checked
                  ? [...form.copay_email_days, i + 1].sort()
                  : form.copay_email_days.filter(x => x !== i + 1))}
              />
              {d.slice(0, 3)}
            </label>
          ))}
        </div>
      )}

      <h3 className="setup-h3">Weekly update email</h3>
      <div className="form-row">
        <div className="form-field full">
          <label>Who gets it</label>
          <input value={form.weekly_report_recipients} onChange={(e) => type('weekly_report_recipients', e.target.value)} onBlur={() => saveRecipients('weekly')} placeholder="owner@practice.com" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Day</label>
          <select value={form.weekly_report_day} onChange={(e) => set('weekly_report_day', e.target.value)}>
            {DAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Hour (practice time)</label>
          <select value={form.weekly_report_hour} onChange={(e) => set('weekly_report_hour', e.target.value)}>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>)}
          </select>
        </div>
      </div>

      <div className="setup-reminders">
        <div className="setup-reminders-title">Atlas Reminders:</div>
        <ul>
          {setup.reminders.map(r => <li key={r}>{r}</li>)}
        </ul>
      </div>
    </div>
  );
}

// --- Summary ----------------------------------------------------------------------

function SummaryStep({ setup, onGo }) {
  const label = Object.fromEntries(STEPS.map(s => [s.key, s.label]));
  const doneCount = setup.checks.filter(c => c.done).length;
  return (
    <div>
      <p className="setup-lead">
        {setup.complete ? 'All done.' : `${doneCount} of ${setup.checks.length} done`}
      </p>
      <ul className="setup-check-list">
        {setup.checks.map(c => (
          <li key={c.key} className={c.done ? 'done' : 'missing'}>
            <span className="setup-check-mark" aria-label={c.done ? 'Done' : 'Not done'}>{c.done ? '✓' : '✗'}</span>
            <span className="setup-check-label">
              {c.label}
              {c.detail && <span className="setup-muted"> · {c.detail}</span>}
            </span>
            {!c.done && (
              <button type="button" className="setup-link" onClick={() => onGo(c.step)}>Go to {label[c.step]}</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PracticeSetupWizard;
